import { useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, ImageOff } from "lucide-react";

const MAX_MB = 5;

export const ImageField = ({ value, onChange, testid = "image-field" }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [broken, setBroken] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`That image is ${(file.size / 1048576).toFixed(1)}MB — keep it under ${MAX_MB}MB`);
      return;
    }
    const body = new FormData();
    body.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/upload", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBroken(false);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2" data-testid={testid}>
      <div className="flex gap-2">
        <Input
          className="rounded-none bg-secondary"
          data-testid={`${testid}-url`}
          placeholder="Paste an image URL or upload"
          value={value || ""}
          onChange={(e) => {
            setBroken(false);
            onChange(e.target.value);
          }}
        />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
        <Button
          type="button"
          variant="outline"
          className="shrink-0 rounded-none"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          data-testid={`${testid}-upload-btn`}
        >
          <Upload className="mr-2 h-4 w-4" /> {busy ? "Uploading…" : "Upload"}
        </Button>
      </div>

      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Preview — exactly how the card will crop it
        </div>
        <div
          className="relative mt-2 h-40 w-full max-w-sm overflow-hidden border border-border bg-secondary/40"
          data-testid={`${testid}-preview`}
        >
          {value && !broken ? (
            <img
              src={value}
              alt="preview"
              className="h-full w-full object-cover opacity-90"
              onError={() => setBroken(true)}
              data-testid={`${testid}-preview-img`}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-xs text-muted-foreground">
              <ImageOff className="h-5 w-5" />
              {broken ? "That URL didn't load" : "No image yet — a category fallback is used"}
            </div>
          )}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Landscape ~800×360 works best, subject centred. JPG, PNG, WebP or GIF up to {MAX_MB}MB.
        </p>
      </div>
    </div>
  );
};
